import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatEther, formatUnits, isAddress, maxUint256, type Address, type Hash } from 'viem';
import { api } from './api';
import { chainCrops, getFarmAddress, ranchFarmAbi, type ChainCrop } from './farmContract';
import { nstChain, publicClient, walletClient } from './wallet';
import { projectTokenAbi } from './projectTokenContract';

type ChainPlot = { cropId: number; plantedAt: number; readyAt: number };
type Props = {
  address: Address;
  nftHolder: boolean;
  onProgress: () => Promise<void>;
  notify: (message: string) => void;
  fail: (error: unknown) => void;
  weather?: { id: string; icon: string; name: string };
};

function remaining(readyAt: number, now: number) {
  const seconds = Math.max(0, readyAt - Math.floor(now / 1000));
  return seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : '成熟';
}

export function ChainFarm({ address, nftHolder, onProgress, notify, fail, weather }: Props) {
  const [selected, setSelected] = useState<ChainCrop>(chainCrops[0]);
  const [runtimeCrops, setRuntimeCrops] = useState<ChainCrop[]>(chainCrops);
  const [plots, setPlots] = useState<ChainPlot[]>(Array.from({ length: 6 }, () => ({ cropId: 0, plantedAt: 0, readyAt: 0 })));
  const [balances, setBalances] = useState<bigint[]>(Array(6).fill(0n));
  const [kava, setKava] = useState<bigint>(0n);
  const [projectToken, setProjectToken] = useState<Address | null>(null);
  const [projectSymbol, setProjectSymbol] = useState('TOKEN');
  const [projectDecimals, setProjectDecimals] = useState(18);
  const [projectBalance, setProjectBalance] = useState(0n);
  const [projectAllowance, setProjectAllowance] = useState(0n);
  const [busy, setBusy] = useState('');
  const [tick, setTick] = useState(Date.now());
  const [burst, setBurst] = useState<number | null>(null);
  const farmAddress = getFarmAddress();
  const configured = Boolean(farmAddress && isAddress(farmAddress));

  const refresh = useCallback(async () => {
    if (!farmAddress || !isAddress(farmAddress)) return;
    const ids = [1n, 2n, 3n, 101n, 102n, 103n];
    const accounts = ids.map(() => address);
    const paymentToken = await publicClient.readContract({ address: farmAddress, abi: ranchFarmAbi, functionName: 'paymentToken' });
    const [tokenBalances, nextPlots, nativeBalance, cropConfigs, tokenSymbol, tokenDecimals, nextProjectBalance, nextProjectAllowance] = await Promise.all([
      publicClient.readContract({ address: farmAddress, abi: ranchFarmAbi, functionName: 'balanceOfBatch', args: [accounts, ids] }),
      Promise.all(Array.from({ length: 6 }, (_, index) => publicClient.readContract({
        address: farmAddress,
        abi: ranchFarmAbi,
        functionName: 'plots',
        args: [address, BigInt(index)]
      }))),
      publicClient.getBalance({ address }),
      Promise.all(chainCrops.map((crop) => publicClient.readContract({
        address: farmAddress,
        abi: ranchFarmAbi,
        functionName: 'cropInfo',
        args: [crop.id]
      }))),
      publicClient.readContract({ address: paymentToken, abi: projectTokenAbi, functionName: 'symbol' }),
      publicClient.readContract({ address: paymentToken, abi: projectTokenAbi, functionName: 'decimals' }),
      publicClient.readContract({ address: paymentToken, abi: projectTokenAbi, functionName: 'balanceOf', args: [address] }),
      publicClient.readContract({ address: paymentToken, abi: projectTokenAbi, functionName: 'allowance', args: [address, farmAddress] })
    ]);
    setBalances([...tokenBalances]);
    setPlots(nextPlots.map(([cropId, plantedAt, readyAt]) => ({
      cropId: Number(cropId), plantedAt: Number(plantedAt), readyAt: Number(readyAt)
    })));
    setKava(nativeBalance);
    setProjectToken(paymentToken);
    setProjectSymbol(tokenSymbol);
    setProjectDecimals(tokenDecimals);
    setProjectBalance(nextProjectBalance);
    setProjectAllowance(nextProjectAllowance);
    const nextCrops = chainCrops.map((crop, index) => {
      const [seedPrice, growSeconds, harvestAmount, valuePoints, active] = cropConfigs[index];
      return { ...crop, seedPrice, growSeconds: Number(growSeconds), harvestAmount: Number(harvestAmount), valuePoints: Number(valuePoints), subtitle: active ? crop.subtitle : '已停用' };
    });
    setRuntimeCrops(nextCrops);
    setSelected((current) => nextCrops.find((crop) => crop.key === current.key) ?? nextCrops[0]);
  }, [address, farmAddress]);

  useEffect(() => {
    refresh().catch(fail);
    const timer = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function confirmed(hash: Hash, action: 'buy' | 'plant' | 'harvest', crop: ChainCrop, plotIndex: number | null) {
    const receipt = await publicClient.waitForTransactionReceipt({ hash, confirmations: 1, timeout: 180_000 });
    if (receipt.status !== 'success') throw new Error('链上交易执行失败');
    await api.syncChainAction(hash, action, crop.key, plotIndex).catch(() => undefined);
    await Promise.all([refresh(), onProgress()]);
  }

  async function buySeeds(crop: ChainCrop) {
    if (!farmAddress || !isAddress(farmAddress)) return;
    setBusy(`buy-${crop.key}`);
    try {
      const gasPrice = await publicClient.getGasPrice();
      const hash = await walletClient(address).writeContract({
        account: address, chain: nstChain, address: farmAddress, abi: ranchFarmAbi,
        functionName: 'buySeeds', args: [crop.id, 1n], gasPrice
      });
      await confirmed(hash, 'buy', crop, null);
      notify(`购得 1 枚${crop.name}种子代币`);
    } catch (error) { fail(error); } finally { setBusy(''); }
  }

  async function approvePaymentToken() {
    if (!farmAddress || !isAddress(farmAddress) || !projectToken) return;
    setBusy('approve-token');
    try {
      const hash = await walletClient(address).writeContract({
        account: address, chain: nstChain, address: projectToken, abi: projectTokenAbi,
        functionName: 'approve', args: [farmAddress, maxUint256], gasPrice: await publicClient.getGasPrice()
      });
      const receipt = await publicClient.waitForTransactionReceipt({ hash, confirmations: 1, timeout: 180_000 });
      if (receipt.status !== 'success') throw new Error('项目代币授权失败');
      await refresh();
      notify(`${projectSymbol} 已授权给链上农场`);
    } catch (error) { fail(error); } finally { setBusy(''); }
  }

  async function plant(plotIndex: number) {
    if (!farmAddress || !isAddress(farmAddress)) return;
    if ((balances[Number(selected.id) - 1] ?? 0n) < 1n) {
      fail(new Error(`需要先购买${selected.name}种子代币`));
      return;
    }
    setBusy(`plot-${plotIndex}`);
    try {
      const gasPrice = await publicClient.getGasPrice();
      const hash = await walletClient(address).writeContract({
        account: address, chain: nstChain, address: farmAddress, abi: ranchFarmAbi,
        functionName: 'plant', args: [BigInt(plotIndex), selected.id], gasPrice
      });
      await confirmed(hash, 'plant', selected, plotIndex);
      notify(`${selected.name}已写入第 ${plotIndex + 1} 块链上农田`);
    } catch (error) { fail(error); } finally { setBusy(''); }
  }

  async function harvest(plotIndex: number, crop: ChainCrop) {
    if (!farmAddress || !isAddress(farmAddress)) return;
    setBusy(`plot-${plotIndex}`);
    try {
      const gasPrice = await publicClient.getGasPrice();
      const hash = await walletClient(address).writeContract({
        account: address, chain: nstChain, address: farmAddress, abi: ranchFarmAbi,
        functionName: 'harvest', args: [BigInt(plotIndex)], gasPrice
      });
      await confirmed(hash, 'harvest', crop, plotIndex);
      setBurst(plotIndex);
      window.setTimeout(() => setBurst(null), 1300);
      notify(`收获 ${crop.harvestAmount}${nftHolder ? ' + NFT 10% 加成' : ''} 枚${crop.name}代币`);
    } catch (error) { fail(error); } finally { setBusy(''); }
  }

  const portfolioPoints = useMemo(() => runtimeCrops.reduce((sum, crop, index) => sum + Number(balances[index + 3] ?? 0n) * crop.valuePoints, 0), [balances, runtimeCrops]);

  return (
    <section className="chain-farm-page">
      <div className="game-hud">
        <div><small>{nstChain.name}</small><b>晨雾山谷 · 链上农场</b></div>
        <div className="hud-resources"><span>🪙 {formatUnits(projectBalance, projectDecimals).slice(0, 10)} {projectSymbol}</span><span>◆ {formatEther(kava).slice(0, 7)} {nstChain.nativeCurrency.symbol} Gas</span><span>✦ {portfolioPoints} 产值</span></div>
      </div>

      <div className="seed-dock">
        <div className="dock-title"><div><small>SEED MARKET</small><b>选择链上种子</b></div><span>{nftHolder ? '💎 创世加成 +10%' : '普通产量'}</span></div>
        <div className="seed-cards">
          {runtimeCrops.map((crop, index) => (
            <article key={crop.key} className={`seed-card ${selected.key === crop.key ? 'selected' : ''}`} onClick={() => setSelected(crop)} style={{ '--crop-color': crop.color } as React.CSSProperties}>
              <div className="seed-art"><img src={crop.image} alt={crop.name} /></div>
              <div className="seed-name"><small>{crop.subtitle}</small><b>{crop.name}</b></div>
              <div className="seed-metrics"><span>种子 {balances[index]?.toString() ?? '0'}</span><span>仓库 {balances[index + 3]?.toString() ?? '0'}</span></div>
              <div className="seed-yield"><b>{crop.multiplier}</b><small>{crop.growSeconds}s · 产出 {crop.harvestAmount}</small></div>
              <button disabled={!configured || Boolean(busy) || projectBalance < crop.seedPrice} onClick={(event) => { event.stopPropagation(); projectAllowance < crop.seedPrice ? approvePaymentToken() : buySeeds(crop); }}>
                {busy === `buy-${crop.key}` || busy === 'approve-token' ? '链上确认…' : projectAllowance < crop.seedPrice ? `授权 ${projectSymbol}` : `${formatUnits(crop.seedPrice, projectDecimals)} ${projectSymbol} 购买`}
              </button>
            </article>
          ))}
        </div>
      </div>

      <div className={`farm-world weather-${weather?.id ?? 'sunny'}`}>
        <img className="farm-world-bg" src="/game-art/farm-valley-v2.webp" alt="晨雾山谷牧场" />
        {weather && <div className="farm-weather-badge">{weather.icon} {weather.name}</div>}
        {weather?.id === 'rain' && <div className="farm-weather-rain" />}
        {weather?.id === 'mist' && <div className="farm-weather-mist" />}
        <div className="world-sunbeam" />
        <div className="world-plots">
          {plots.map((plot, index) => {
            const crop = runtimeCrops.find((item) => Number(item.id) === plot.cropId);
            const ready = Boolean(crop && plot.readyAt <= Math.floor(tick / 1000));
            const progress = crop && plot.readyAt > plot.plantedAt
              ? Math.max(0.18, Math.min(1, (tick / 1000 - plot.plantedAt) / (plot.readyAt - plot.plantedAt))) : 0;
            return (
              <button
                key={index}
                className={`world-plot ${crop ? 'occupied' : 'vacant'} ${ready ? 'harvestable' : ''} ${burst === index ? 'burst' : ''}`}
                disabled={!configured || Boolean(busy) || Boolean(crop && !ready)}
                onClick={() => crop ? harvest(index, crop) : plant(index)}
                aria-label={crop ? `${crop.name}${ready ? '可收获' : '生长中'}` : `第${index + 1}块农田，种植${selected.name}`}
              >
                {crop ? <>
                  <img className="growing-crop" src={crop.image} alt="" style={{ '--growth': progress } as React.CSSProperties} />
                  <span className="crop-timer">{busy === `plot-${index}` ? '链上确认…' : ready ? '✨ 收获' : remaining(plot.readyAt, tick)}</span>
                  {!ready && <i className="growth-bar"><em style={{ width: `${progress * 100}%` }} /></i>}
                </> : <span className="plant-hint"><b>＋</b><small>种植</small></span>}
                {burst === index && <span className="harvest-burst">✦ ✧ ✦</span>}
              </button>
            );
          })}
        </div>
        {!configured && <div className="farm-contract-notice"><b>链上农场合约待部署</b><span>画面与交互已就绪，部署后开放真实买种、播种和收获</span></div>}
        <div className="world-tip">已选择 <b>{selected.name}</b> · 点击空农田播种</div>
      </div>

      <div className="token-ledger">
        <div><span>🌾</span><small>小麦收获物</small><b>{balances[3]?.toString() ?? '0'}</b></div>
        <div><span>🌽</span><small>玉米收获物</small><b>{balances[4]?.toString() ?? '0'}</b></div>
        <div><span>🍓</span><small>星露莓收获物</small><b>{balances[5]?.toString() ?? '0'}</b></div>
      </div>
      <p className="farm-chain-note">种子使用项目方部署的标准 ERC-20 购买；授权、买种、播种和收获均由钱包签名并记录上链。产出倍率仅代表游戏数量，不承诺现实收益。</p>
    </section>
  );
}
